// Vue template for dataset_distribution_display.html

<template>
    <div>
        <div v-if="loaded">
            <div>
                <h3 class="groups-header">
                    Data Sets and Distributions
                    <button title="You can also hover over the left border and change the size of the tree."
                        class='btn btn-outline-info btn-sm pull-right' @click="toggleTree">
                        {{ showTree ? "Hide tree" : "Show tree" }}
                    </button>
                </h3>

                <div class="panel panel-default panel-heading dataset-breadcrumbs" id='breadcrumbs'>
                    <span v-for="(group, index) in reversedCrumbs" :key="group.uuid">
                        <strong v-if="index > 0"> / </strong>
                        <a v-if="index === 0 || index < reversedCrumbs.length - 1"
                            @click="selectCrumbs(selectedCrumbs.slice(selectedCrumbs.length - (index+1)))" >
                            {{ group.dsName ? group.dsName : group.name }}
                        </a>
                        <em v-else>
                            {{ group.dsName ? group.dsName : group.name }}
                        </em>
                    </span>
                </div>
            </div>

            <div class="display-container" ref="displayContainer">
                <vue-resizable v-show="showTree" class="tree-display" :active="['r']"
                    @resize:end="handleTreeResize" :width="treeWidth" :maxWidth="maxTreeWidth" :height="treeHeight">
                    <div class="panel panel-default" ref="treeDisplay">
                        <div class="panel-body">
                            <dataset-tree :id="objId" :is-root="true" :givenRoot="root" @selectCrumbs="selectCrumbs" />
                        </div>
                    </div>
                </vue-resizable>

                <div class="group-display panel panel-default" ref="groupDisplay" :style="{ minHeight: minGroupHeight + 'px' }">
                    <dataset-distribution-group @selectGroup="selectDisplayGroup" 
                        :group="selectedCrumbs[0]" :is-root="root === selectedCrumbs[0]" />
                </div>
            </div>
        </div>

        <Loading v-else class='full-display' />
    </div>
</template>

<script>
import Loading from 'src/components/Loading.vue'
import DatasetDistributionGroup from 'src/components/dataset/components/DatasetDistributionGroup.vue'
import DatasetTree from 'src/components/dataset/components/DatasetTree.vue'
import VueResizable from 'vue-resizable'
import fetchUtils from '../fetchUtils'

export default {
    name: "DatasetDistributionGroupDisplay",
    components: {
        Loading,
        DatasetDistributionGroup,
        DatasetTree,
        VueResizable,
    },
    mixins: [fetchUtils],
    props: {
        objId: {
            type: String,
            required: true,
        }
    },
    data: () => ({
        root: {},
        loaded: false,
        showTree: false,
        selectedCrumbs: [], // array of groups, starting with the currently selected group and going up to root
        treeWidth: 1,
        maxTreeWidth: 800,
        treeHeight: 1,
        minGroupHeight: 0,
    }),
    computed: {
        reversedCrumbs: function() {
            return this.selectedCrumbs.slice().reverse()
        }
    },
    created: async function() {
        this.root = await this.fetchDataset(this.objId)
        
        this.selectCrumbs([this.root])

        this.loaded = true
    },
    mounted: async function() {
        if (this.loaded) {
            this.onLoad()
        } else {
            // wait for loaded
            const unwatch = this.$watch("loaded", val => {
                if (val) {
                    this.onLoad()
                    unwatch()
                }
            })
        }
    },
    methods: {
        onLoad: function() {
            // run once DOM and dataset loaded
            this.startObserver()

            if (this.root.groups.length > 0) {
                this.toggleTree()
            }
        },
        selectCrumbs: function(crumbs) {
            // update crumbs
            this.selectedCrumbs = crumbs
        },
        selectDisplayGroup: function(group) {
            // selects group shown in DatasetDistributionGroup
            this.selectedCrumbs.unshift(group)
        },
        toggleTree: function() {
            if (this.showTree) {
                if (this.$refs.treeDisplay.offsetWidth < 80) {
                    // set treeWidth, indicating tree should be set to default width on show
                    this.treeWidth = 1
                }
            } else {
                if (this.treeWidth == 1) {
                    // set treeWidth to default, forcing vue-resizable to reload with new width
                    this.treeWidth = window.innerWidth * 0.15
                }
            }

            this.showTree = !this.showTree
        },
        startObserver: function() {
            // observe group display for height changes
            const observer = new ResizeObserver(() => {
                this.handleGroupResize()
            })
            observer.observe(this.$refs.groupDisplay)
        },
        handleTreeResize: function(rect) {
            // hide tree if resized to small width
            if (this.showTree && rect.width <= 30) {
                this.toggleTree()
            }
        },
        handleGroupResize: function() {
            // update max tree width
            this.maxTreeWidth = this.$refs.displayContainer.offsetWidth

            // match tree height to display height
            this.treeHeight = this.$refs.groupDisplay.offsetHeight

            // increase minimum height to the current display height
            if (this.$refs.groupDisplay.offsetHeight > this.minGroupHeight) {
                this.minGroupHeight = this.$refs.groupDisplay.offsetHeight
            }
        },
    }
}
</script>

<style scoped>
.groups-header {
    padding-bottom: 5px;
}

.dataset-breadcrumbs {
    margin-bottom: -5px;
    font-size: 90%;
}

.display-container {
    display: flex;
    width: 100%;
    height: 100%;
    max-height: 800px;
}

.tree-display {
    flex-shrink: 0;
    max-height: 100%;
    font-size: 90%;
}

.tree-display .panel {
    width: 100%;
    height: 100%;
    overflow: auto;
}

.group-display {
    flex-grow: 1;
    overflow-x: auto;
    overflow-y: auto;
}

.full-display {
    width: 100%;
    display: inline-block;
}

a {
    cursor: pointer;
}
</style>
